import { useRef } from 'react';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { Upload, Download } from 'lucide-react';
import { ImportType } from '../BulkImport';
import { downloadSampleCSV } from '../../../utils/csvParser';

interface UploadStepProps {
  importType: ImportType;
  onFileUpload: (file: File) => void;
}

export const UploadStep = ({ importType, onFileUpload }: UploadStepProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      onFileUpload(file);
    }
  };

  const getImportTypeLabel = () => {
    switch (importType) {
      case 'students':
        return 'Students';
      case 'parents':
        return 'Parents';
      case 'community':
        return 'Community Members';
      case 'fees':
        return 'Fees';
      case 'courses':
        return 'Courses';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload {getImportTypeLabel()} CSV File
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Upload a CSV file containing {getImportTypeLabel().toLowerCase()} data. You can download a sample CSV file to see the expected format.
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => downloadSampleCSV(importType)}
        >
          Download Sample CSV
        </Button>
      </Box>

      <Paper
        sx={{
          p: 6,
          border: '2px dashed',
          borderColor: 'primary.main',
          bgcolor: 'background.default',
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
        <Typography variant="h6" gutterBottom>
          Drop CSV file here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supports CSV files only
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          CSV Format Requirements:
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>First row must contain column headers</li>
            <li>Use commas to separate values</li>
            <li>Wrap values containing commas in double quotes</li>
            <li>Ensure consistent number of columns across all rows</li>
          </ul>
        </Typography>
      </Box>
    </Box>
  );
};
